import { InvalidCatalogDefinitionError, UnknownMemberError } from "./errors.model";
import { assertFactCondition, isFactCondition, type PatientFactSource } from "./lib/patient-facts";
import { answersByQuestion, disabledMemberIds } from "./lib/questionnaire-conditions";
import { orderByTriggers, type TriggerShape } from "./lib/questionnaire-order";
import {
  computeQuestionSetScores,
  implicitTotalScore,
  normalizedBands,
  scoreRange,
  validateQuestionnaireScores,
} from "./lib/questionnaire-scoring";
import {
  assignMemberIds,
  highWaterMark,
  type Member,
  type MemberId,
  type MemberIdSequence,
  memberIdSequence,
} from "./member-identity";
import type { QuestionnaireAnswer } from "./questionnaire-instance.model";
import type { QuestionnaireScoreCalculation } from "./questionnaire-score-calculation";
import { CatalogDefinition, type CatalogSpine } from "./spine.model";
import { type QuestionnaireAnswerValue, validateValue } from "./value.model";

/** Shared ordered Q&A content, identities and scoring for patient questionnaires and clinician measures. */
/** Comparison an enablement condition applies. `exists` tests presence, not equality. */
export type QuestionnaireConditionOperator = "=" | "!=" | ">" | "<" | ">=" | "<=" | "exists";

/**
 * What a condition compares against. A VALUE, replacing the four loose
 * `answerString | answerInteger | answerBoolean | answerDecimal` fields the
 * FHIR-shaped entity carried — one of which was always set and the reader had
 * to guess which. A `Choice` comparison names the option's system-assigned
 * identity, never its label, because a label is content an author may correct.
 */
export type QuestionnaireConditionValue =
  | { kind: "Boolean"; value: boolean }
  | { kind: "Integer"; value: number }
  | { kind: "Quantity"; value: number }
  | { kind: "Text"; value: string }
  | { kind: "Date"; value: string }
  | { kind: "Choice"; optionId: MemberId };

export type PatientFact = "age";
export interface QuestionnaireFactCondition {
  fact: PatientFact;
  operator: "=" | "!=" | ">" | "<" | ">=" | "<=";
  value: number;
}
export type EnablementCondition = QuestionnaireCondition | QuestionnaireFactCondition;

export interface QuestionnaireCondition {
  /** The question whose answer is tested. Answer-producing, and never inside a repeating group. */
  question: MemberId;
  operator: QuestionnaireConditionOperator;
  value: QuestionnaireConditionValue;
}

/** Whether every condition must hold or any one of them. Absent reads as `all`, FHIR's own default. */
export type QuestionnaireConditionBehavior = "any" | "all";

interface Conditional {
  conditions?: readonly EnablementCondition[];
  conditionBehavior?: QuestionnaireConditionBehavior;
}

/** A question that produces an answer. The only item kind a condition may reference. */
export interface QuestionnaireQuestion extends Member, Conditional {
  kind: "Question";
  label: string;
  help?: string;
  /** Required PER OCCURRENCE THAT EXISTS. Whether a repeating group needs any occurrence is `minOccurrences`. */
  required: boolean;
  value: QuestionnaireAnswerValue;
  /** Asked as Yes/No before the options. Absent reads as false. */
  askYesNoFirst?: boolean;
}

/** Instructional copy. Produces no answer, so nothing can be conditioned on it. */
interface QuestionnaireDisplay extends Member {
  kind: "Display";
  text: string;
}

/**
 * "List each previous surgery: date, procedure, surgeon" — a genuine intake
 * pattern a flat model can only express by hardcoding "surgery 1, surgery 2".
 *
 * ONE LEVEL ONLY, and it carries no conditions of its own: an occurrence-count
 * that depends on an answer is a second way to say `minOccurrences`.
 */
interface QuestionnaireRepeatingGroup extends Member {
  kind: "RepeatingGroup";
  label: string;
  /** How many occurrences must exist. Zero means the group may be left empty. */
  minOccurrences: number;
  items: readonly (QuestionnaireQuestion | QuestionnaireDisplay)[];
}

export type QuestionnaireItem = QuestionnaireQuestion | QuestionnaireDisplay | QuestionnaireRepeatingGroup;

/** Sections carry conditions, so a whole section can be hidden. */
export interface QuestionnaireSection extends Member, Conditional {
  label: string;
  /** Asked as one screen rather than one question at a time. Absent reads as false. */
  showTogether?: boolean;
  items: readonly QuestionnaireItem[];
}

/**
 * One recorded answer, as the caller of `completionRequirements` knows it. A
 * plain scalar rather than the instance's own value type: enablement compares,
 * it does not interpret, and a `Choice` answer compares as its option identity.
 */
export interface QuestionnaireAnswerFact {
  questionId: MemberId;
  /** Which repetition of a repeating group this answer belongs to. Absent everywhere else. */
  occurrence?: number;
  value: boolean | number | string | undefined;
}

/** A question that must be answered, at the occurrence it must be answered for. */
export interface RequiredAnswerSlot {
  questionId: MemberId;
  occurrence?: number;
}

/**
 * The high-water marks the three member namespaces have issued.
 *
 * PERSISTED STATE, not a count of what is present. Reseeding a sequence from
 * the live members would reuse a removed member's identity the moment the
 * removed one was the highest — the exact failure system-assigned ids exist to
 * prevent, and past answers cite those ids.
 */
export interface QuestionnaireMemberMarks {
  section: number;
  item: number;
  option: number;
  score: number;
}

/**
 * A stored revision as persistence actually holds it. The marks were written
 * as one JSON string and only later at all, so a row may carry none of them or
 * only some. Translating what is there is the codec's; filling what is not is
 * `restore`'s.
 */
export interface StoredQuestionnaireDefinitionProps extends Omit<QuestionnaireDefinitionState, "marks" | "scores"> {
  marks?: Partial<QuestionnaireMemberMarks>;
  scores?: readonly QuestionnaireScore[];
}

export interface NewQuestionnaireDefinition {
  url: string;
  mintedAt: string;
  name: string;
  description?: string;
  sections: readonly QuestionnaireSection[];
}

/** Inclusive `to` ends a band; omitted, it runs to the next start (exclusive), or the maximum. */
export interface QuestionnaireScoreBand {
  label: string;
  from: number;
  to?: number;
}

/**
 * How a score combines its items. Absent sums them. `percent-change` compares
 * the second item against the first, `(second − first) / first × 100`, as a
 * dual-task cost does, so its two items are ordered baseline then comparison.
 */
export type { QuestionnaireScoreCalculation } from "./questionnaire-score-calculation";

export interface QuestionnaireScore extends Member {
  label: string;
  itemIds: readonly MemberId[];
  bands?: readonly QuestionnaireScoreBand[];
  calculation?: QuestionnaireScoreCalculation;
}

/** Everything a mapper needs to put a stored revision back together. */
export interface QuestionnaireDefinitionState extends CatalogSpine {
  sections: readonly QuestionnaireSection[];
  marks: QuestionnaireMemberMarks;
  scores: readonly QuestionnaireScore[];
  /** The derived Total is never persisted, so its bands are stored on their own. */
  totalBands?: readonly QuestionnaireScoreBand[];
}

const SECTION_PREFIX = "sec";
const ITEM_PREFIX = "q";
const OPTION_PREFIX = "opt";

export abstract class QuestionSetDefinition<Self extends QuestionSetDefinition<Self>> extends CatalogDefinition<Self> {
  private scoreList: readonly QuestionnaireScore[];
  private totalBandList: readonly QuestionnaireScoreBand[] | undefined;
  private sectionList: readonly QuestionnaireSection[];
  private marks: QuestionnaireMemberMarks;

  protected get numericScoreValues(): boolean {
    return false;
  }

  protected constructor(
    spine: CatalogSpine,
    sections: readonly QuestionnaireSection[],
    marks: QuestionnaireMemberMarks,
    scores: readonly QuestionnaireScore[] = [],
    totalBands?: readonly QuestionnaireScoreBand[],
  ) {
    super(spine);
    // Known ids are the incoming ones: on `create` there are none, and on
    // `rehydrate` storage is the authority on what this lineage has already
    // issued. Only `setSections` checks an argument against the ids the
    // aggregate already holds, because only there can a caller be stale.
    //
    // The marks are raised to clear anything already present, because a stored
    // revision written before the marks were persisted has none — and a
    // sequence starting below a live member would reissue that member's
    // identity to a different question on the very next setter.
    const known = currentIds(sections);
    const assigned = assignSections(sections, known, sequences(atLeastPresent(marks, known)));
    const scoreSequence = memberIdSequence("score", Math.max(marks.score ?? 0, highWaterMark(scores)));
    const assignedScores = assignMemberIds(
      scores,
      new Set(scores.flatMap((score) => (score.id ? [score.id] : []))),
      scoreSequence,
      "score",
    );
    const banded = withNormalizedBands(assignedScores);
    const total = normalizedBands(totalBands);
    validateQuestionnaireScores(banded, assigned.sections, total, this.numericScoreValues);
    validateSections(assigned.sections, spine.url, this.asksNothingAllowed());
    this.sectionList = assigned.sections;
    this.marks = { ...assigned.marks, score: scoreSequence.high };
    this.scoreList = structuredClone(banded);
    this.totalBandList = structuredClone(total);
  }

  get sections(): readonly QuestionnaireSection[] {
    return structuredClone(this.sectionList) as readonly QuestionnaireSection[];
  }

  /**
   * Whether a revision may ask no question at all. A questionnaire that asks
   * nothing can never be completed; a set whose answer lies elsewhere — a
   * consent form's is the signature — has no such problem.
   */
  protected asksNothingAllowed(): boolean {
    return false;
  }

  /**
   * The questions a rule may test: every top-level question. Questions inside
   * repeating groups are left out, as a form's own enablement conditions do.
   */
  get askableQuestionIds(): ReadonlySet<MemberId> {
    const ids = new Set<MemberId>();
    for (const section of this.sectionList) {
      for (const item of section.items) {
        if (item.kind === "Question" && item.id) ids.add(item.id);
      }
    }
    return ids;
  }

  /** Persist these alongside the sections; see `QuestionnaireMemberMarks`. */
  get memberMarks(): QuestionnaireMemberMarks {
    return { ...this.marks };
  }

  snapshot(): QuestionnaireDefinitionState {
    return this.facts;
  }

  /** Persisted facts: scores contains authored subscores only; use `scores` for the effective score list. */
  get facts(): QuestionnaireDefinitionState {
    return {
      ...this.spineFacts,
      sections: this.sections,
      marks: this.memberMarks,
      scores: structuredClone(this.scoreList),
      ...(this.totalBandList ? { totalBands: structuredClone(this.totalBandList) } : {}),
    };
  }

  protected substantiveFacts(): unknown {
    return { sections: this.sectionList, scores: this.scoreList, totalBands: this.totalBandList };
  }

  /**
   * Replace the whole structure. ONE SETTER, not add/remove/reorder commands:
   * a questionnaire's one cross-member rule is that conditions reference other
   * questions, and the entity enforces that ON THE RESULT — whatever sections
   * it is handed, a condition pointing at a question that is not there is
   * rejected. Nine commands would not make that rule stronger, they would give
   * it nine entry points.
   *
   * IDENTITIES ARE CARRIED, NEVER REMINTED. A member arriving with a known id
   * is that member, edited; one arriving with none is new and gets one; one
   * arriving with an id this lineage does not recognise is an error rather
   * than an invitation to create it. Reminting would silently repoint every
   * recorded answer, which cites the question and option identity.
   */
  setSections(
    sections: readonly QuestionnaireSection[],
    scores: readonly QuestionnaireScore[] = this.scoreList,
    totalBands: readonly QuestionnaireScoreBand[] | undefined = this.totalBandList,
  ): void {
    const assigned = assignSections(sections, currentIds(this.sectionList), sequences(this.marks));
    const sequence = memberIdSequence("score", this.marks.score);
    const assignedScores = assignMemberIds(
      scores,
      new Set(this.scoreList.map((score) => score.id!)),
      sequence,
      "score",
    );
    const banded = withNormalizedBands(assignedScores);
    const total = normalizedBands(totalBands);
    validateQuestionnaireScores(banded, assigned.sections, total, this.numericScoreValues);
    validateSections(assigned.sections, this.url, this.asksNothingAllowed());
    this.sectionList = assigned.sections;
    this.scoreList = structuredClone(banded);
    this.totalBandList = structuredClone(total);
    this.marks = { ...assigned.marks, score: sequence.high };
  }

  get scores(): readonly QuestionnaireScore[] {
    const total = implicitTotalScore(this.sectionList, this.numericScoreValues, this.scoreList);
    const banded = total && this.totalBandList ? { ...total, bands: this.totalBandList } : total;
    return structuredClone(banded ? [banded, ...this.scoreList] : this.scoreList);
  }

  setScores(
    scores: readonly QuestionnaireScore[],
    totalBands: readonly QuestionnaireScoreBand[] | undefined = this.totalBandList,
  ): void {
    this.setSections(this.sections, scores, totalBands);
  }

  scoreMinimum(scoreId: MemberId): number {
    return scoreRange(this.declaredScore(scoreId), this.sectionList).minimum;
  }

  private declaredScore(scoreId: MemberId): QuestionnaireScore {
    const score = this.scores.find((candidate) => candidate.id === scoreId);
    if (!score) throw new UnknownMemberError(`Unknown score '${scoreId}'`, { memberId: scoreId, collection: "score" });
    return score;
  }

  scoreMaximum(scoreId: MemberId): number {
    const score = this.scores.find((candidate) => candidate.id === scoreId);
    if (!score) throw new UnknownMemberError(`Unknown score '${scoreId}'`, { memberId: scoreId, collection: "score" });
    return scoreRange(score, this.sectionList).maximum;
  }

  computeScores(answers: readonly QuestionnaireAnswer[]) {
    return computeQuestionSetScores(this.sectionList, this.scores, answers);
  }

  /**
   * Which answers are still owed, given what has been answered so far.
   *
   * Walks the tree respecting PARENT ENABLEMENT — a required question in a
   * hidden section is not owed — and counts a required question only when its
   * own conditions are satisfied. Carried forward unchanged in spirit from the
   * entity this replaces, which already got this right; what is new is the
   * occurrence, because an answer inside a repeating group is question
   * identity PLUS occurrence.
   *
   * A group's occurrence count is whichever is larger: the occurrences that
   * have been answered, or `minOccurrences`. So a group nobody has touched
   * still owes its first occurrence when one is required.
   */
  completionRequirements(
    answers: readonly QuestionnaireAnswerFact[],
    facts: PatientFactSource,
  ): readonly RequiredAnswerSlot[] {
    const byQuestion = answersByQuestion(answers);
    const disabled = disabledMemberIds(this.sectionList, answers, facts);
    const enabled = (member: { id?: MemberId }): boolean => !member.id || !disabled.has(member.id);

    const owed: RequiredAnswerSlot[] = [];
    for (const section of this.sectionList) {
      if (!enabled(section)) continue;
      for (const item of section.items) {
        if (item.kind === "Display") continue;
        if (item.kind === "Question") {
          if (item.required && enabled(item)) {
            owed.push({ questionId: item.id as MemberId });
          }
          continue;
        }
        for (let occurrence = 0; occurrence < occurrenceCount(item, byQuestion); occurrence += 1) {
          for (const member of item.items) {
            if (member.kind !== "Question" || !member.required) continue;
            if (enabled(member)) owed.push({ questionId: member.id as MemberId, occurrence });
          }
        }
      }
    }
    return owed;
  }

  /**
   * Questions currently hidden by their own or their section's conditions —
   * including those whose condition tests a question that is itself hidden.
   */
  disabledQuestionIds(answers: readonly QuestionnaireAnswerFact[], facts: PatientFactSource): readonly MemberId[] {
    const disabled = disabledMemberIds(this.sectionList, answers, facts);
    const questions: MemberId[] = [];
    for (const section of this.sectionList) {
      for (const item of section.items) {
        const members = item.kind === "RepeatingGroup" ? item.items : [item];
        for (const member of members) {
          if (member.kind === "Question" && member.id && disabled.has(member.id)) questions.push(member.id);
        }
      }
    }
    return questions;
  }
}

// ============================================================
// Member identity assignment
// ============================================================

interface KnownIds {
  sections: Set<MemberId>;
  items: Set<MemberId>;
  options: Set<MemberId>;
}

interface Sequences {
  section: MemberIdSequence;
  item: MemberIdSequence;
  option: MemberIdSequence;
}

export function assertNoAuthoredMemberIds(sections: readonly QuestionnaireSection[]): void {
  const assertConditions = (conditional: Conditional): void => {
    const condition = conditional.conditions?.find((condition) => !isFactCondition(condition));
    if (!condition || isFactCondition(condition)) return;
    throw new UnknownMemberError(`No condition question with identity '${condition.question}'`, {
      memberId: condition.question,
      collection: "condition question",
    });
  };
  for (const section of sections) {
    assertConditions(section);
    for (const item of section.items) {
      if (item.kind === "Question") assertConditions(item);
      if (item.kind === "RepeatingGroup") {
        for (const child of item.items) if (child.kind === "Question") assertConditions(child);
      }
    }
  }
  const supplied = currentIds(sections);
  const first = supplied.sections.values().next().value;
  if (first)
    throw new UnknownMemberError(`No section member with identity '${first}'`, {
      memberId: first,
      collection: "section",
    });
  const item = supplied.items.values().next().value;
  if (item)
    throw new UnknownMemberError(`No item member with identity '${item}'`, { memberId: item, collection: "item" });
  const option = supplied.options.values().next().value;
  if (option)
    throw new UnknownMemberError(`No option member with identity '${option}'`, {
      memberId: option,
      collection: "option",
    });
}

/**
 * The stored marks, raised to cover every identity actually present. A
 * rehydration fallback only: the persisted mark is the authority, because it
 * also remembers the members that were REMOVED, which nothing in the tree can.
 */
function atLeastPresent(marks: QuestionnaireMemberMarks, known: KnownIds): QuestionnaireMemberMarks {
  return {
    score: marks.score,
    section: Math.max(marks.section, highWaterMark([...known.sections].map((id) => ({ id })))),
    item: Math.max(marks.item, highWaterMark([...known.items].map((id) => ({ id })))),
    option: Math.max(marks.option, highWaterMark([...known.options].map((id) => ({ id })))),
  };
}

function sequences(marks: QuestionnaireMemberMarks): Sequences {
  return {
    section: memberIdSequence(SECTION_PREFIX, marks.section),
    item: memberIdSequence(ITEM_PREFIX, marks.item),
    option: memberIdSequence(OPTION_PREFIX, marks.option),
  };
}

/**
 * Every identity the lineage currently holds, flattened. Flat on purpose:
 * moving a question from one section to another, or into a repeating group, is
 * a reorganisation of the same question and must preserve its identity — a
 * per-section known set would reject the move as an unrecognised id.
 */
function currentIds(sections: readonly QuestionnaireSection[]): KnownIds {
  const known: KnownIds = { sections: new Set(), items: new Set(), options: new Set() };
  for (const section of sections) {
    if (section.id) known.sections.add(section.id);
    for (const item of section.items) {
      collectItemIds(item, known);
      if (item.kind === "RepeatingGroup") for (const member of item.items) collectItemIds(member, known);
    }
  }
  return known;
}

function collectItemIds(item: QuestionnaireItem, known: KnownIds): void {
  if (item.id) known.items.add(item.id);
  if (item.kind === "Question" && item.value.kind === "Choice") {
    for (const option of item.value.options) if (option.id) known.options.add(option.id);
  }
}

function assignSections(
  incoming: readonly QuestionnaireSection[],
  known: KnownIds,
  seq: Sequences,
): { sections: readonly QuestionnaireSection[]; marks: QuestionnaireMemberMarks } {
  const sections = assignMemberIds(incoming, known.sections, seq.section, "section").map((section) => ({
    ...section,
    items: assignItems(section.items, known, seq),
  }));
  return { sections, marks: { section: seq.section.high, item: seq.item.high, option: seq.option.high, score: 0 } };
}

function assignItems<T extends QuestionnaireItem>(incoming: readonly T[], known: KnownIds, seq: Sequences): T[] {
  return assignMemberIds(incoming, known.items, seq.item, "item").map((item) => {
    if (item.kind === "Question" && item.value.kind === "Choice") {
      return {
        ...item,
        value: {
          ...item.value,
          options: assignMemberIds(item.value.options, known.options, seq.option, "option"),
        },
      } as T;
    }
    if (item.kind === "RepeatingGroup") {
      return { ...item, items: assignItems(item.items, known, seq) } as T;
    }
    return item;
  });
}

// ============================================================
// Validation
// ============================================================

function validateSections(sections: readonly QuestionnaireSection[], url: string, asksNothingAllowed: boolean): void {
  if (sections.length === 0 && !asksNothingAllowed) {
    throw new InvalidCatalogDefinitionError("A questionnaire requires at least one section", { url });
  }

  const seenIds = new Set<string>();
  const questions = new Map<MemberId, { question: QuestionnaireQuestion; insideGroup: boolean }>();
  let answerProducing = 0;

  const claim = (id: MemberId | undefined, collection: string): void => {
    if (!id) return;
    if (seenIds.has(id)) {
      throw new InvalidCatalogDefinitionError(`Member '${id}' appears twice in the questionnaire`, {
        url,
        memberId: id,
        collection,
      });
    }
    seenIds.add(id);
  };

  for (const section of sections) {
    claim(section.id, "section");
    if (section.label.trim() === "") {
      throw new InvalidCatalogDefinitionError("A section must have a label", { url, memberId: section.id });
    }
    if (section.items.length === 0) {
      throw new InvalidCatalogDefinitionError(`Section '${section.label}' has no items`, { url, memberId: section.id });
    }
    for (const item of section.items) {
      claim(item.id, "item");
      validateItem(item, url);
      if (item.kind === "Question") {
        answerProducing += 1;
        questions.set(item.id as MemberId, { question: item, insideGroup: false });
      }
      if (item.kind === "RepeatingGroup") {
        for (const member of item.items) {
          claim(member.id, "item");
          validateItem(member, url);
          if (member.kind === "Question") {
            answerProducing += 1;
            questions.set(member.id as MemberId, { question: member, insideGroup: true });
          }
        }
      }
    }
  }

  // The escape hatch a questionnaire cannot do without: a form of nothing but
  // instructional copy collects no answer, so nothing can ever complete it.
  if (answerProducing === 0 && !asksNothingAllowed) {
    throw new InvalidCatalogDefinitionError("A questionnaire requires at least one question", { url });
  }

  validateConditions(sections, questions, url);
  assertFollowUpsAfterTriggers(sections, url);
}

function validateItem(item: QuestionnaireItem, url: string): void {
  if (item.kind === "Display") {
    if (item.text.trim() === "") {
      throw new InvalidCatalogDefinitionError("A display item must have text", { url, memberId: item.id });
    }
    return;
  }
  if (item.kind === "Question") {
    if (item.label.trim() === "") {
      throw new InvalidCatalogDefinitionError("A question must have a label", { url, memberId: item.id });
    }
    validateValue(item.value, { memberId: item.id, collection: "item" });
    if (
      item.askYesNoFirst &&
      (item.value.kind !== "Choice" || item.value.cardinality !== "Single" || item.value.options.length < 2)
    ) {
      throw new InvalidCatalogDefinitionError(
        `Question '${item.label}' can ask Yes/No first only as a single-select choice with at least two options`,
        { url, memberId: item.id },
      );
    }
    return;
  }
  if (item.label.trim() === "") {
    throw new InvalidCatalogDefinitionError("A repeating group must have a label", { url, memberId: item.id });
  }
  if (!Number.isInteger(item.minOccurrences) || item.minOccurrences < 0) {
    throw new InvalidCatalogDefinitionError(
      `A repeating group's minOccurrences must be a non-negative whole number, got ${item.minOccurrences}`,
      { url, memberId: item.id },
    );
  }
  if (item.items.length === 0) {
    throw new InvalidCatalogDefinitionError(`Repeating group '${item.label}' has no items`, { url, memberId: item.id });
  }
}

function validateConditions(
  sections: readonly QuestionnaireSection[],
  questions: ReadonlyMap<MemberId, { question: QuestionnaireQuestion; insideGroup: boolean }>,
  url: string,
): void {
  // Nodes are sections and questions alike, because enablement composes: a
  // question is only asked when its section is shown, so a section's own
  // condition is an edge every question inside it inherits. Leave the parent
  // edges out and a cycle that runs through a section reads as acyclic and
  // hangs the charting UI at render time instead.
  const dependencies = new Map<MemberId, MemberId[]>();
  const holders: Array<{ id: MemberId; conditional: Conditional }> = [];

  for (const section of sections) {
    holders.push({ id: section.id as MemberId, conditional: section });
    for (const item of section.items) {
      const members = item.kind === "RepeatingGroup" ? item.items : [item];
      for (const member of members) {
        if (member.kind !== "Question") continue;
        holders.push({ id: member.id as MemberId, conditional: member });
        dependencies.set(member.id as MemberId, [section.id as MemberId]);
      }
    }
  }

  for (const holder of holders) {
    const edges = dependencies.get(holder.id) ?? [];
    for (const condition of holder.conditional.conditions ?? []) {
      if (isFactCondition(condition)) {
        assertFactCondition(condition);
        continue;
      }
      const target = questions.get(condition.question);
      if (!target) {
        throw new InvalidCatalogDefinitionError(
          `Condition on '${holder.id}' references '${condition.question}', which is not an answer-producing question`,
          { url, memberId: holder.id },
        );
      }
      // Which occurrence would it test? FHIR is loose here and implementations
      // disagree, so this closes it rather than leaving the answer to whoever
      // renders the form.
      if (target.insideGroup) {
        throw new InvalidCatalogDefinitionError(
          `Condition on '${holder.id}' references '${condition.question}', which is inside a repeating group`,
          { url, memberId: holder.id },
        );
      }
      if (condition.question === holder.id) {
        throw new InvalidCatalogDefinitionError(`'${holder.id}' cannot depend on itself`, { url, memberId: holder.id });
      }
      if (condition.operator === "exists" && condition.value.kind !== "Boolean") {
        throw new InvalidCatalogDefinitionError(
          `An 'exists' condition compares presence, so its value must be a boolean, not ${condition.value.kind}`,
          { url, memberId: holder.id },
        );
      }
      edges.push(condition.question);
    }
    dependencies.set(holder.id, edges);
  }

  const visiting = new Set<MemberId>();
  const visited = new Set<MemberId>();
  const walk = (id: MemberId): void => {
    if (visiting.has(id)) {
      throw new InvalidCatalogDefinitionError("Conditional dependency cycle in the questionnaire", {
        url,
        memberId: id,
      });
    }
    if (visited.has(id)) return;
    visiting.add(id);
    for (const next of dependencies.get(id) ?? []) walk(next);
    visiting.delete(id);
    visited.add(id);
  };
  for (const id of dependencies.keys()) walk(id);
}

// ============================================================
// Enablement
// ============================================================

function occurrenceCount(
  group: QuestionnaireRepeatingGroup,
  answers: ReadonlyMap<MemberId, QuestionnaireAnswerFact[]>,
): number {
  let answered = 0;
  for (const member of group.items) {
    if (member.kind !== "Question" || !member.id) continue;
    // An answer to a question inside a repeating group carries its repetition.
    // A missing one is read as the first: absent `occurrence` is how a question
    // outside any group is addressed, so an answer that reaches here without it
    // is one the caller declined to number, not a second repetition.
    for (const answer of answers.get(member.id) ?? []) answered = Math.max(answered, (answer.occurrence ?? 0) + 1);
  }
  return Math.max(answered, group.minOccurrences);
}

function withNormalizedBands(scores: readonly QuestionnaireScore[]): QuestionnaireScore[] {
  return scores.map(({ bands, ...score }) => {
    const normalized = normalizedBands(bands);
    return normalized ? { ...score, bands: normalized } : score;
  });
}

const TRIGGER_SHAPE: TriggerShape<QuestionnaireSection, QuestionnaireItem> = {
  items: (section) => section.items,
  withItems: (section, items) => ({ ...section, items }),
  answers: (item) =>
    (item.kind === "Question"
      ? [item.id]
      : item.kind === "RepeatingGroup"
        ? item.items.map((child) => child.id)
        : []
    ).filter((id): id is MemberId => id !== undefined),
  triggers: (node) =>
    "conditions" in node
      ? (node.conditions ?? []).flatMap((condition) => (isFactCondition(condition) ? [] : [condition.question]))
      : [],
};

/**
 * A follow-up is asked straight after the answer that raises it, so a
 * definition that puts one anywhere else is refused rather than reordered:
 * the builder places them, and a revision is the only way order changes, so
 * an in-progress response keeps the order it pinned.
 */
function assertFollowUpsAfterTriggers(sections: readonly QuestionnaireSection[], url: string): void {
  const flat = (list: readonly QuestionnaireSection[]) =>
    list.flatMap((section) => [section, ...section.items] as (QuestionnaireSection | QuestionnaireItem)[]);
  const authored = flat(sections).map((member) => member.id);
  const misplaced = flat(orderByTriggers(sections, TRIGGER_SHAPE)).filter((member, at) => authored[at] !== member.id);
  // Name the follow-up that moved, not whatever it pushed aside.
  const moved = misplaced.find((member) => TRIGGER_SHAPE.triggers(member).length > 0) ?? misplaced[0];
  if (!moved) return;
  const label = "label" in moved ? moved.label : moved.text;
  throw new InvalidCatalogDefinitionError(`'${label}' depends on an earlier question and must come right after it`, {
    url,
    memberId: moved.id,
  });
}
